import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  Switch,
  Typography,
  Space,
  Skeleton,
  message,
  Table,
  Button,
  Input,
  Tag,
  Modal,
  Alert,
  Tooltip,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { MessageOutlined, SafetyCertificateOutlined, PlusOutlined } from '@ant-design/icons';
import { api } from '../api';
import { t } from '../i18n';

type CertAuthority = Awaited<ReturnType<typeof api.listCertAuthorities>>['authorities'][number];

/**
 * Global system settings (superadmin only): the feedback-chat toggle and the
 * disk-certificate authority registry (built-in "Our" + generated clone CAs).
 */
export function Settings() {
  const qc = useQueryClient();
  const settings = useQuery({ queryKey: ['settings'], queryFn: api.getSettings });

  const update = useMutation({
    mutationFn: (patch: { feedbackEnabled?: boolean }) => api.updateSettings(patch),
    onSuccess: (data) => {
      qc.setQueryData(['settings'], data);
      qc.invalidateQueries({ queryKey: ['server-info'] });
      message.success(t('settingsSaved'));
    },
    onError: (err: Error) => message.error(err.message),
  });

  const feedbackEnabled = settings.data?.feedbackEnabled ?? true;

  return (
    <Space direction="vertical" size={16} style={{ width: '100%', maxWidth: 900 }}>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
        {t('settingsIntro')}
      </Typography.Paragraph>

      <Card title={t('settingsFeedbackTitle')}>
        {settings.isLoading ? (
          <Skeleton active paragraph={{ rows: 1 }} />
        ) : (
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
            <div style={{ minWidth: 0 }}>
              <Typography.Text strong>
                <MessageOutlined style={{ marginRight: 8 }} />
                {t('settingsFeedbackLabel')}
              </Typography.Text>
              <Typography.Paragraph type="secondary" style={{ marginTop: 4, marginBottom: 0 }}>
                {t('settingsFeedbackHint')}
              </Typography.Paragraph>
            </div>
            <Switch
              checked={feedbackEnabled}
              loading={update.isPending}
              onChange={(v) => update.mutate({ feedbackEnabled: v })}
            />
          </div>
        )}
      </Card>

      <CertAuthoritiesCard />
    </Space>
  );
}

function CertAuthoritiesCard() {
  const qc = useQueryClient();
  const casQ = useQuery({ queryKey: ['cert-authorities'], queryFn: api.listCertAuthorities });
  const [newName, setNewName] = useState('');
  const [renameTarget, setRenameTarget] = useState<CertAuthority | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<CertAuthority | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState('');

  const refresh = () => qc.invalidateQueries({ queryKey: ['cert-authorities'] });

  const create = useMutation({
    mutationFn: (name: string) => api.createCertAuthority(name),
    onSuccess: () => {
      setNewName('');
      refresh();
      message.success(t('certCaCreated'));
    },
    onError: (e: Error) => message.error(e.message),
  });
  const patch = useMutation({
    mutationFn: (v: { id: string; name?: string; setDefault?: boolean }) =>
      api.updateCertAuthority(v.id, { name: v.name, setDefault: v.setDefault }),
    onSuccess: () => {
      setRenameTarget(null);
      refresh();
    },
    onError: (e: Error) => message.error(e.message),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.deleteCertAuthority(id),
    onSuccess: (r) => {
      setDeleteTarget(null);
      setDeleteConfirm('');
      refresh();
      message.success(t('certCaDeleted', { n: r.flashCount }));
    },
    onError: (e: Error) => message.error(e.message),
  });

  const columns: ColumnsType<CertAuthority> = [
    {
      title: t('certCaName'),
      key: 'name',
      render: (_, c) => (
        <Space size={6}>
          <SafetyCertificateOutlined />
          <span style={{ fontWeight: 600 }}>{c.name}</span>
          {c.isDefault && <Tag color="blue">{t('certCaDefault')}</Tag>}
          {c.builtin && <Tag>{t('certCaBuiltin')}</Tag>}
        </Space>
      ),
    },
    {
      title: t('certCaFingerprint'),
      key: 'fp',
      render: (_, c) => (
        <code style={{ fontSize: 11 }}>{c.fingerprint.slice(0, 23)}…</code>
      ),
    },
    {
      title: t('certCaFlashed'),
      key: 'flashed',
      width: 110,
      render: (_, c) => <span>{c.flashCount}</span>,
    },
    {
      title: '',
      key: 'actions',
      width: 260,
      render: (_, c) => (
        <Space size={4} wrap>
          {!c.isDefault && (
            <Button size="small" onClick={() => patch.mutate({ id: c.id, setDefault: true })}>
              {t('certCaSetDefault')}
            </Button>
          )}
          {!c.builtin && (
            <Button
              size="small"
              onClick={() => {
                setRenameTarget(c);
                setRenameValue(c.name);
              }}
            >
              {t('certCaRename')}
            </Button>
          )}
          {!c.builtin && (
            <Button
              size="small"
              danger
              onClick={() => {
                setDeleteTarget(c);
                setDeleteConfirm('');
              }}
            >
              {t('certCaDelete')}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card title={t('settingsCertsTitle')}>
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        {t('settingsCertsIntro')}
      </Typography.Paragraph>

      <Space.Compact style={{ marginBottom: 12, width: '100%', maxWidth: 460 }}>
        <Input
          placeholder={t('certCaNamePlaceholder')}
          value={newName}
          maxLength={40}
          onChange={(e) => setNewName(e.target.value)}
          onPressEnter={() => newName.trim() && create.mutate(newName.trim())}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          loading={create.isPending}
          disabled={!newName.trim()}
          onClick={() => create.mutate(newName.trim())}
        >
          {t('certCaGenerate')}
        </Button>
      </Space.Compact>

      <Table<CertAuthority>
        rowKey="id"
        size="small"
        loading={casQ.isLoading}
        columns={columns}
        dataSource={casQ.data?.authorities ?? []}
        pagination={false}
      />

      {/* Rename */}
      <Modal
        open={!!renameTarget}
        title={t('certCaRename')}
        okText={t('certCaRename')}
        onOk={() => renameTarget && patch.mutate({ id: renameTarget.id, name: renameValue.trim() })}
        okButtonProps={{ disabled: !renameValue.trim(), loading: patch.isPending }}
        onCancel={() => setRenameTarget(null)}
      >
        <Input value={renameValue} maxLength={40} onChange={(e) => setRenameValue(e.target.value)} />
      </Modal>

      {/* Delete — three warnings + typed confirmation (superadmin). */}
      <Modal
        open={!!deleteTarget}
        title={t('certCaDeleteTitle', { name: deleteTarget?.name ?? '' })}
        okText={t('certCaDelete')}
        okButtonProps={{
          danger: true,
          disabled: deleteConfirm !== deleteTarget?.name,
          loading: remove.isPending,
        }}
        onOk={() => deleteTarget && remove.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      >
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          <Alert
            type="warning"
            showIcon
            message={t('certCaDeleteWarn1', { n: deleteTarget?.flashCount ?? 0 })}
          />
          <Alert type="warning" showIcon message={t('certCaDeleteWarn2')} />
          <Alert type="error" showIcon message={t('certCaDeleteWarn3')} />
          <div>
            <Tooltip title={t('certCaDeleteConfirmHint', { name: deleteTarget?.name ?? '' })}>
              <Typography.Text>{t('certCaDeleteConfirmLabel')}</Typography.Text>
            </Tooltip>
            <Input
              autoFocus
              value={deleteConfirm}
              placeholder={deleteTarget?.name}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              style={{ marginTop: 6 }}
            />
          </div>
        </Space>
      </Modal>
    </Card>
  );
}
